import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
// @ts-ignore
import heic2any from 'heic2any';

export interface PhotoCaptureResult {
  file: File;
  base64: string;
  preview: SafeUrl;
}

export interface MultiplePhotoCaptureResult {
  files: PhotoCaptureResult[];
  totalCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PhotoCaptureService {
  
  private readonly maxFileSize = 10 * 1024 * 1024; // 10MB
  private readonly supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  private readonly compressionQuality = 0.8;
  private readonly maxDimension = 1920;

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Detecta se o dispositivo tem câmera
   */
  async detectDeviceCapabilities(): Promise<{ isMobile: boolean; hasCamera: boolean }> {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    let hasCamera = false;
    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        hasCamera = devices.some(device => device.kind === 'videoinput');
      } else {
        hasCamera = isMobile; // Assumir que móveis têm câmera
      }
    } catch {
      hasCamera = isMobile;
    }

    return { isMobile, hasCamera };
  }

  /**
   * Abre a câmera nativa (funciona em todos os dispositivos)
   */
  async openNativeCamera(): Promise<PhotoCaptureResult> {
    return this.createFileInput(true);
  }

  /**
   * Abre o seletor de arquivos
   */
  async openFileSelector(): Promise<PhotoCaptureResult> {
    return this.createFileInput(false);
  }

  /**
   * Abre o seletor de arquivos múltiplos
   */
  async openMultipleFileSelector(): Promise<MultiplePhotoCaptureResult> {
    return this.createMultipleFileInput(false);
  }

  /**
   * Cria input de arquivo e processa resultado
   */
  private createFileInput(useCamera: boolean): Promise<PhotoCaptureResult> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      // Adiciona suporte para HEIC no accept
      input.accept = this.supportedFormats.concat(['.heic', '.HEIC', '.heif', '.HEIF']).join(',');
      input.style.display = 'none';
      
      if (useCamera) {
        input.setAttribute('capture', 'environment');
      }
      
      input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (target.files?.[0]) {
          try {
            const result = await this.processFile(target.files[0]);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('Nenhum arquivo selecionado'));
        }
        this.cleanup(input);
      };
      
      input.oncancel = () => {
        reject(new Error('Operação cancelada'));
        this.cleanup(input);
      };
      
      document.body.appendChild(input);
      input.click();
    });
  }

  /**
   * Cria input de arquivo múltiplo e processa resultado
   */
  private createMultipleFileInput(useCamera: boolean): Promise<MultiplePhotoCaptureResult> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true; // Permite seleção múltipla
      // Adiciona suporte para HEIC no accept
      input.accept = this.supportedFormats.concat(['.heic', '.HEIC', '.heif', '.HEIF']).join(',');
      input.style.display = 'none';
      
      if (useCamera) {
        input.setAttribute('capture', 'environment');
      }
      
      input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          try {
            const results: PhotoCaptureResult[] = [];
            const files = Array.from(target.files);
            
            // Processar cada arquivo
            for (const file of files) {
              try {
                const result = await this.processFile(file);
                results.push(result);
              } catch (error) {
                console.error(`Erro ao processar arquivo ${file.name}:`, error);
                // Continua processando outros arquivos mesmo se um falhar
              }
            }
            
            if (results.length === 0) {
              reject(new Error('Nenhum arquivo válido foi processado'));
            } else {
              resolve({
                files: results,
                totalCount: results.length
              });
            }
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('Nenhum arquivo selecionado'));
        }
        this.cleanup(input);
      };
      
      input.oncancel = () => {
        reject(new Error('Operação cancelada'));
        this.cleanup(input);
      };
      
      document.body.appendChild(input);
      input.click();
    });
  }

  /**
   * Processa múltiplos arquivos com callback de progresso
   */
  public async processMultipleFiles(
    files: File[], 
    progressCallback?: (current: number, total: number, fileName: string) => void
  ): Promise<PhotoCaptureResult[]> {
    const results: PhotoCaptureResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        if (progressCallback) {
          progressCallback(i + 1, files.length, file.name);
        }
        
        const result = await this.processFile(file);
        results.push(result);
      } catch (error) {
        console.error(`Erro ao processar arquivo ${file.name}:`, error);
        // Continua processando outros arquivos mesmo se um falhar
      }
    }
    
    return results;
  }

  /**
   * Processa o arquivo selecionado
   */
  public async processFile(file: File): Promise<PhotoCaptureResult> {
    // Validar arquivo
    this.validateFile(file);
    
    // Converter HEIC para JPEG se necessário
    let fileToProcess = file;
    if (this.isHeicFile(file)) {
      fileToProcess = await this.convertHeicToJpeg(file);
    }
    
    // Processar imagem
    const processedFile = await this.compressImage(fileToProcess);
    
    // Converter para base64
    const base64 = await this.fileToBase64(processedFile);
    
    // Criar preview
    const preview = this.sanitizer.bypassSecurityTrustUrl(base64);

    return { file: processedFile, base64, preview };
  }

  /**
   * Valida o arquivo
   */
  private validateFile(file: File): void {
    // Aceita também arquivos HEIC mesmo sem tipo MIME correto (iOS às vezes não define)
    const isHeic = this.isHeicFile(file);
    if (!this.supportedFormats.includes(file.type) && !isHeic) {
      throw new Error('Tipo de imagem não suportado. Use JPEG, PNG, WebP ou HEIC.');
    }
    
    if (file.size > this.maxFileSize) {
      throw new Error('Arquivo muito grande. Máximo 10MB.');
    }
    
    if (file.size === 0) {
      throw new Error('Arquivo vazio ou corrompido.');
    }
  }

  /**
   * Comprime a imagem se necessário.
   *
   * IMPORTANTE — LEAK DE MEMÓRIA: a `URL.createObjectURL(file)` reserva um slot
   * na memória do navegador até ser explicitamente revogado. Sem `URL.revokeObjectURL`,
   * cada foto processada deixava memória presa até o unload da página (sintomas
   * fortes em mobile no fluxo de upload via QR Code, onde o browser reinicializa
   * pouca memória). Revogamos em TODOS os caminhos de saída (onload, onerror, try/catch).
   */
  private compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const objectUrl = URL.createObjectURL(file);

      const cleanup = () => {
        try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
      };

      img.onload = () => {
        try {
          // Calcular dimensões
          let { width, height } = img;
          if (width > this.maxDimension || height > this.maxDimension) {
            const ratio = Math.min(this.maxDimension / width, this.maxDimension / height);
            width *= ratio;
            height *= ratio;
          }

          // Redimensionar
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          // Converter para blob — a URL.revokeObjectURL() tem que acontecer
          // ANTES do resolve/reject (podem ser síncronos na cadeia subsequente).
          canvas.toBlob(
            (blob) => {
              cleanup();
              if (blob) {
                resolve(new File([blob], file.name, { type: 'image/jpeg' }));
              } else {
                reject(new Error('Falha ao processar imagem'));
              }
            },
            'image/jpeg',
            this.compressionQuality
          );
        } catch (error) {
          cleanup();
          reject(error);
        }
      };

      img.onerror = () => {
        cleanup();
        reject(new Error('Falha ao carregar imagem'));
      };
      img.src = objectUrl;
    });
  }

  /**
   * Converte arquivo para base64
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Remove input do DOM
   */
  private cleanup(input: HTMLInputElement): void {
    if (document.body.contains(input)) {
      document.body.removeChild(input);
    }
  }

  /**
   * Verifica se o arquivo é HEIC/HEIF
   */
  private isHeicFile(file: File | Blob): boolean {
    const name = (file as File).name || '';
    const type = file.type || '';
    return (
      type === 'image/heic' ||
      type === 'image/heif' ||
      name.toLowerCase().endsWith('.heic') ||
      name.toLowerCase().endsWith('.heif')
    );
  }

  /**
   * Converte arquivo HEIC para JPEG
   */
  private async convertHeicToJpeg(file: File | Blob): Promise<File> {
    try {
      
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: this.compressionQuality
      }) as Blob;

      // Criar novo arquivo com nome atualizado
      const fileName = (file as File).name || 'image.jpg';
      const newFileName = fileName.replace(/\.heic$|\.heif$/i, '.jpg');
      
      return new File([convertedBlob], newFileName, { type: 'image/jpeg' });
    } catch (error) {
      console.error('Erro ao converter HEIC:', error);
      throw new Error('Não foi possível converter a imagem HEIC. Tente outro formato.');
    }
  }

  /**
   * Converte base64 HEIC para JPEG (para imagens vindas do backend)
   */
  public async convertBase64HeicToJpeg(base64Data: string): Promise<string> {
    try {
      // Detectar se é HEIC analisando os primeiros bytes
      const isHeic = await this.detectHeicFromBase64(base64Data);
      
      if (!isHeic) {
        // Se não for HEIC, retorna como está
        return "data:image/jpeg;base64," + base64Data;
      }

      
      // Remove o prefixo data:image se existir
      const base64Clean = base64Data.replace(/^data:.*,/, '');
      
      // Converter base64 para blob
      const byteCharacters = atob(base64Clean);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/heic' });
      
      // Converter HEIC para JPEG
      const jpegFile = await this.convertHeicToJpeg(blob);
      
      // Converter de volta para base64
      return await this.fileToBase64(jpegFile);
    } catch (error) {
      console.info('Erro ao converter base64 HEIC:', error);
      // Em caso de erro, retorna o original
      return "data:image/jpeg;base64," + base64Data;
    }
  }

  /**
   * Detecta se um base64 é HEIC verificando os magic bytes
   */
  public async detectHeicFromBase64(base64Data: string): Promise<boolean> {
    try {
      // Remove o prefixo data:image se existir
      const base64Clean = base64Data.replace(/^data:.*,/, '');
      
      // Decodifica os primeiros bytes
      const binaryString = atob(base64Clean.substring(0, 100)); // Pega só o início para análise
      
      // HEIC files geralmente começam com 'ftyp' no offset 4
      // e contém 'heic', 'heix', 'hevc', ou 'hevx' logo depois
      const ftypIndex = binaryString.indexOf('ftyp');
      if (ftypIndex !== -1 && ftypIndex < 20) {
        const heicSignatures = ['heic', 'heix', 'hevc', 'hevx', 'mif1'];
        return heicSignatures.some(sig => binaryString.indexOf(sig) !== -1);
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Processa uma imagem base64 vinda do backend
   * Converte HEIC para JPEG se necessário
   */
  public async processBackendImage(base64Data: string): Promise<SafeUrl> {
    try {
      // Tenta converter se for HEIC
      const processedBase64 = await this.convertBase64HeicToJpeg(base64Data);
      
      // Garante que tem o prefixo data:image
      let finalBase64 = processedBase64;
      if (!finalBase64.startsWith('data:')) {
        finalBase64 = `data:image/jpeg;base64,${processedBase64}`;
      }
      
      return this.sanitizer.bypassSecurityTrustUrl(finalBase64);
    } catch (error) {
      console.error('Erro ao processar imagem do backend:', error);
      // Em caso de erro, tenta usar a imagem original
      const fallbackBase64 = base64Data.startsWith('data:') 
        ? base64Data 
        : `data:image/jpeg;base64,${base64Data}`;
      return this.sanitizer.bypassSecurityTrustUrl(fallbackBase64);
    }
  }

  public isUrl(photo: string): boolean {
    const regex = /^(https?:\/\/)?(www\.)?([-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})\b([-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*)$/i
    return regex.test(photo);
  }
} 